/**
 * Sorun kayıtları (ADR 0149): giriş denemeleri ve sunucu hataları için ortak tipler.
 * Bulut uygulamaları control veritabanına yazar; gemi sunucusu önce yerel `_server_events`'e
 * yazar, senkron turunda buluta gönderir. Panelin Sorunlar sayfası ikisini birlikte gösterir.
 */

export const SIGN_IN_SURFACES = [
  'office',
  'panel',
  'supplier',
  'vessel_lan',
  'vessel_local',
] as const;
export type SignInSurface = (typeof SIGN_IN_SURFACES)[number];

export const SIGN_IN_STEPS = [
  'password',
  'totp',
  'totp_setup',
  'recovery_code',
  'reauth',
  'password_change',
  'pairing_code',
  'add_device',
] as const;
export type SignInStep = (typeof SIGN_IN_STEPS)[number];

/** `blocked`: deneme sınırı ya da kilit yüzünden parola hiç denenmedi. */
export const SIGN_IN_RESULTS = ['success', 'failure', 'blocked'] as const;
export type SignInResult = (typeof SIGN_IN_RESULTS)[number];

/**
 * İç neden kodları. Kullanıcıya dönen koddan ayrıntılıdır (ör. INVALID_CREDENTIALS'ın hangi
 * nedenle döndüğü); yalnız kayıtta ve panelde görünür, istemciye gönderilmez.
 */
export const SIGN_IN_REASONS = [
  'unknown_account',
  'wrong_password',
  'account_disabled',
  'no_password',
  'no_assignment',
  'not_on_vessel',
  'tenant_unavailable',
  'tenant_not_active',
  'temp_password_expired',
  'password_change_required',
  'wrong_current_password',
  'totp_invalid',
  'totp_reused',
  'recovery_code_invalid',
  'throttled',
  'locked',
  'invalid_code',
  'code_expired',
  'server_identity_mismatch',
  'not_activated',
  'forbidden',
  'wrong_stage',
  'origin_mismatch',
  'validation_failed',
  'internal_error',
] as const;
export type SignInReason = (typeof SIGN_IN_REASONS)[number];

/** Kaydedilecek tek giriş denemesi. Parola ya da kod hiçbir alanda tutulmaz. */
export interface SignInEventInput {
  /** Gemide üretilen kimlik; yeniden gönderimde aynı olay iki kez yazılmaz. */
  id?: string;
  at?: Date;
  surface: SignInSurface;
  step: SignInStep;
  result: SignInResult;
  reason?: SignInReason | null;
  /** Kullanıcıya dönen hata kodu (ör. INVALID_CREDENTIALS); başarıda null. */
  responseCode?: string | null;
  /** Yazılan ham kimlik; kayda yalnız e-posta biçimindeyse küçük harfle girer. */
  identifier?: string | null;
  userId?: string | null;
  tenantId?: string | null;
  vesselId?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  appVersion?: string | null;
  detail?: Record<string, unknown> | null;
}

export const PROBLEM_APPS = ['api', 'worker', 'admin', 'supplier', 'ship-server'] as const;
export type ProblemApp = (typeof PROBLEM_APPS)[number];

/** `log`: süreç içi günlük; `stderr`: PM2 hata günlüğü dinleyicisi; `vessel`: gemiden gelen. */
export type ErrorSource = 'log' | 'stderr' | 'vessel';
export type ErrorLevel = 'warn' | 'error' | 'fatal';

/** Yakalanan tek hata. Metinler kaydedilmeden önce sırlardan arındırılır ve kırpılır. */
export interface CapturedError {
  at: Date;
  app: ProblemApp;
  source: ErrorSource;
  level: ErrorLevel;
  /** err.type/err.name ve varsa ':' + err.code (ör. 'DomainError:TENANT_NOT_ACTIVE'). */
  kind: string | null;
  /** İlk satır, en çok 500 karakter. */
  message: string;
  /** 'POST /api/office/auth/login' biçiminde, sorgu dizesi olmadan. */
  route: string | null;
  statusCode: number | null;
  /** En çok 4000 karakter. */
  stack: string | null;
  reqId: string | null;
  host: string | null;
  version: string | null;
  tenantId: string | null;
  vesselId: string | null;
  detail: Record<string, unknown> | null;
}

export const ERROR_GROUP_STATUSES = ['open', 'resolved', 'muted'] as const;
export type ErrorGroupStatus = (typeof ERROR_GROUP_STATUSES)[number];

/** Gruplama anahtarı eklenmiş hata; control veritabanına bu biçimde yazılır. */
export interface FingerprintedError extends CapturedError {
  fingerprint: string;
}

/**
 * Noktalı alan adı ve harfli üst alan (ya da punycode) şart: 'Felix@2026', 'P@ssw0rd!' gibi
 * e-posta alanına yazılmış parolalar e-posta sayılmaz.
 */
const EMAIL_SHAPE =
  /^[^\s@]{1,64}@(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9-]{1,59})$/;

export interface SignInIdentifierFlags {
  /** Başta ya da sonda boşluk vardı (kopyala-yapıştır hatası). */
  trimmed?: true;
  /** E-posta biçiminde değildi; değerin kendisi de uzunluğu da tutulmaz. */
  notEmail?: true;
}

/**
 * Yazılan kimliği kayda hazırlar. Yalnız e-posta biçimindeki değer kırpılıp küçük harfle tutulur;
 * e-posta alanına yanlışlıkla yazılmış bir parola kayda girmesin diye başka biçimde yalnız işaret kalır.
 */
export function normalizeSignInIdentifier(raw: unknown): {
  email: string | null;
  flags: SignInIdentifierFlags;
} {
  if (typeof raw !== 'string' || raw.length === 0) return { email: null, flags: {} };
  const trimmed = raw.trim();
  const flags: SignInIdentifierFlags = trimmed === raw ? {} : { trimmed: true };
  if (!EMAIL_SHAPE.test(trimmed)) {
    return { email: null, flags: { ...flags, notEmail: true } };
  }
  return { email: trimmed.toLowerCase(), flags };
}

const TEMPORARY_PASSWORD_SHAPE = /^([A-Za-z0-9]{4}-){3}[A-Za-z0-9]{4}$/;

/**
 * Parolanın kendisini ya da uzunluğunu kayda koymadan sorun gidermeye yarayan iki işaret:
 * başta/sonda boşluk (WhatsApp'tan kopyalama) ve geçici parola biçimi (Abcd-Efgh-2345-mnpq).
 */
export function passwordHints(password: unknown): {
  passwordWhitespace?: true;
  temporaryShape?: true;
} {
  if (typeof password !== 'string' || password.length === 0) return {};
  const hints: { passwordWhitespace?: true; temporaryShape?: true } = {};
  if (password.trim() !== password) hints.passwordWhitespace = true;
  if (TEMPORARY_PASSWORD_SHAPE.test(password.trim())) hints.temporaryShape = true;
  return hints;
}

// ---------------------------------------------------------------------------------------------
// Temizleme, rota, parmak izi ve günlük ayrıştırma (ADR 0149). Bulut uygulamaları, worker'daki
// stderr dinleyicisi ve gemi sunucusu aynı kuralları kullanır: bir hata nerede yakalanırsa
// yakalansın aynı biçimde temizlenir, kırpılır ve gruplanır.
// ---------------------------------------------------------------------------------------------

/** Kayıt ve gemi gönderimi metin sınırları (gemi → bulut sözleşmesiyle aynı). */
export const PROBLEM_LIMITS = {
  message: 500,
  stack: 4000,
  kind: 120,
  route: 200,
  version: 40,
  reqId: 100,
  host: 255,
  detailJson: 2048,
  responseCode: 64,
  email: 320,
  ip: 64,
  device: 120,
} as const;

/** Maskelenen değerin yerine yazılan metin. */
export const REDACTED_TEXT = '<redacted>';

/**
 * Yakalayıcının kendi düz metin iletilerinin öneki. Bu önekle başlayan günlük ve stderr satırları
 * yeniden yakalanmaz (döngü yok).
 */
export const PROBLEMS_LOG_PREFIX = '[problems]';

const UUID_PATTERN = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const UUID_ONLY = new RegExp(`^${UUID_PATTERN}$`);
const UUID_ANYWHERE = new RegExp(UUID_PATTERN, 'g');

/**
 * scheme://kullanıcı:parola@ — kullanıcı adı kalır, parola maskelenir. Uzunluk sınırları noktalı
 * uzun metinde (a.a.a…) geri izlemeyi doğrusal tutar.
 */
const URL_CREDENTIALS =
  /\b([A-Za-z][A-Za-z0-9+.-]{0,31}:\/\/)([^\s:@/?#]{0,128}):([^\s@/?#]{0,512})@/g;
/** Authorization şeması ve belirteci. */
const AUTH_SCHEME = /\b(Bearer|Basic)(\s+)([A-Za-z0-9._~+/-]+=*)/gi;
/** JWT benzeri: base64url `{"` (eyJ) ile başlayan, noktalı bölümler. */
const JWT_LIKE = /\beyJ[A-Za-z0-9_-]{2,}(?:\.[A-Za-z0-9_-]*){0,2}/g;
/** Tireli 4'erli gruplar: geçici ve okunabilir parola (Abcd-Efgh-2345-mnpq). UUID'ye uymaz. */
const GROUPED_PASSWORD = /(?<![A-Za-z0-9-])[A-Za-z0-9]{4}(?:-[A-Za-z0-9]{4}){3,}(?![A-Za-z0-9-])/g;
/** Eşleştirme kodu (ABCD-EF23) ve kurtarma kodu (7K2QX-9MZ4P): yalnız büyük harf ve rakam. */
const PAIRING_CODE = /(?<![A-Za-z0-9-])[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}(?![A-Za-z0-9-])/g;
const RECOVERY_CODE = /(?<![A-Za-z0-9-])[0-9A-Z]{5}-[0-9A-Z]{5}(?![A-Za-z0-9-])/g;
/** Node'un Buffer görünümü (`<Buffer 73 61 … 12 more bytes>`): baytlar parola olabilir. */
const BUFFER_VIEW = /<Buffer(?: [0-9a-f]{2})*(?: \.\.\. \d+ more bytes?)?>/g;
/**
 * 32+ karakterlik base64/base64url/hex dizisi; maskelenip maskelenmeyeceğine `looksRandomRun`
 * karar verir.
 */
const LONG_RUN = /(?<![A-Za-z0-9_+/=-])[A-Za-z0-9_+/-]{32,}={0,2}(?![A-Za-z0-9_+/=-])/g;
/**
 * anahtar=değer, anahtar: değer, "anahtar":"değer" (kaçışlı tırnak dahil). Anahtar sözcüklere
 * bölünüp `isSecretKey` ile sınanır; değerin sınırını `secretValueSpan` bulur.
 */
const KEY_SEPARATOR =
  /(?<![A-Za-z0-9])(?:\\?["'])?([A-Za-z][A-Za-z0-9_.-]{0,79})(?:\\?["'])?[ \t]*[=:][ \t]*/g;
const UNQUOTED_VALUE = /[^\s&;,"'`})\]]+/y;
/** Boşluklu değerde sonraki sözcük yeni bir anahtarsa (` user=ali`, ` --pwd=x`) değer orada biter. */
const NEXT_KEY = /(?:--?)?(?:\\?["'])?[A-Za-z][A-Za-z0-9_.-]{0,79}(?:\\?["'])?[ \t]*[=:]/y;
/**
 * `code` anahtarında kalan değerler: hata kodu biçimi (ERR_INVALID_ARG_TYPE, TENANT_NOT_ACTIVE) ve
 * küçük harfli doğrulama kodu (too_small). TOTP, eşleştirme ve kurtarma kodlarında alt çizgi yok.
 */
const ERROR_CODE_VALUE = /^(?:[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+|[a-z]+(?:_[a-z]+)+)$/;

/** Anahtarın son sözcüğü bunlardan biriyse değeri sırdır (accessToken, FELIX_DB_PASSWORD). */
const SECRET_WORDS = new Set([
  'password',
  'passwd',
  'pwd',
  'pass',
  'passphrase',
  'secret',
  'token',
  'apikey',
  'authorization',
  'cookie',
  'session',
  'totp',
  'otp',
]);
/** Tek sözcüklü bileşik anahtar (PGPASSWORD, CLIENTSECRET, APITOKEN) bunları içeriyorsa sırdır. */
const SECRET_STEMS = /password|passwd|secret|token/;
/** `…Code` yalnız bu öneklerle sırdır; statusCode, errorCode, responseCode kalır. */
const SECRET_CODE_PREFIXES = new Set([
  'totp',
  'otp',
  'mfa',
  'pairing',
  'recovery',
  'verification',
  'auth',
  'reset',
  'activation',
  'backup',
]);
/** `…Key` yalnız bu öneklerle sırdır (apiKey ve x-api-key ayrıca; privateKey). */
const SECRET_KEY_PREFIXES = new Set(['private', 'secret', 'signing', 'access', 'encryption']);
/** Değeri satır sonuna kadar süren başlıklar ("Cookie: a=1; b=2", "Authorization: Token x"). */
const WHOLE_LINE_WORDS = new Set(['authorization', 'cookie']);

function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_.-]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.toLowerCase());
}

function isSecretKey(words: readonly string[]): boolean {
  const last = words.at(-1);
  if (last === undefined) return false;
  if (SECRET_WORDS.has(last) || SECRET_STEMS.test(last)) return true;
  const previous = words.at(-2);
  if (last === 'code') return previous === undefined || SECRET_CODE_PREFIXES.has(previous);
  if (last === 'key') {
    return (
      previous !== undefined && (previous.endsWith('api') || SECRET_KEY_PREFIXES.has(previous))
    );
  }
  return last === 'id' && previous === 'session';
}

function endOfLine(text: string, from: number): number {
  const newline = text.indexOf('\n', from);
  const end = newline === -1 ? text.length : newline;
  return end > from && text[end - 1] === '\r' ? end - 1 : end;
}

function span(from: number, to: number): [number, number] | null {
  return to > from ? [from, to] : null;
}

/** Ayraçtan sonra başlayan değerin [başlangıç, bitiş) aralığı; tırnaklar aralığa girmez. */
function secretValueSpan(text: string, start: number, wholeLine: boolean): [number, number] | null {
  const first = text[start];
  if (first === undefined) return null;
  const lineEnd = endOfLine(text, start);
  if (first === '\\' && (text[start + 1] === '"' || text[start + 1] === "'")) {
    const close = text.indexOf(`\\${text[start + 1]}`, start + 2);
    return span(start + 2, close === -1 || close > lineEnd ? lineEnd : close);
  }
  if (first === '"' || first === "'" || first === '`') {
    let i = start + 1;
    while (i < lineEnd && text[i] !== first) i += text[i] === '\\' ? 2 : 1;
    return span(start + 1, Math.min(i, lineEnd));
  }
  if (first === '[') {
    const close = text.indexOf(']', start + 1);
    return span(start + 1, close === -1 || close > lineEnd ? lineEnd : close);
  }
  if (wholeLine) {
    let end = lineEnd;
    while (end > start && /\s/.test(text[end - 1] ?? '')) end -= 1;
    return span(start, end);
  }
  return span(start, unquotedValueEnd(text, start, lineEnd));
}

/**
 * Tırnaksız değer boşluklu olabilir ("password=sahte uzun parola"): satır sonu, `,` `;` `&` `}`
 * ya da anahtara benzeyen sonraki sözcüğe kadar sürer.
 */
function unquotedValueEnd(text: string, start: number, lineEnd: number): number {
  let end = start;
  let at = start;
  for (;;) {
    UNQUOTED_VALUE.lastIndex = at;
    const word = UNQUOTED_VALUE.exec(text);
    if (word === null || at >= lineEnd) return end;
    end = at + word[0].length;
    let next = end;
    while (next < lineEnd && (text[next] === ' ' || text[next] === '\t')) next += 1;
    if (next === end || next >= lineEnd) return end;
    NEXT_KEY.lastIndex = next;
    if (NEXT_KEY.test(text)) return end;
    at = next;
  }
}

function redactKeyValues(text: string): string {
  let out = '';
  let copied = 0;
  KEY_SEPARATOR.lastIndex = 0;
  for (let match = KEY_SEPARATOR.exec(text); match !== null; match = KEY_SEPARATOR.exec(text)) {
    const words = keyWords(match[1] ?? '');
    if (!isSecretKey(words)) continue;
    const last = words.at(-1) ?? '';
    const range = secretValueSpan(text, match.index + match[0].length, WHOLE_LINE_WORDS.has(last));
    if (range === null) continue;
    const value = text.slice(range[0], range[1]);
    if (value === REDACTED_TEXT || (last === 'code' && ERROR_CODE_VALUE.test(value))) continue;
    out += text.slice(copied, range[0]) + REDACTED_TEXT;
    copied = range[1];
    KEY_SEPARATOR.lastIndex = range[1];
  }
  return copied === 0 ? text : out + text.slice(copied);
}

/**
 * Rastgele görünen dizi: saf hex ya da harf ve rakam karışık; ayraç (- _) varsa büyük ve küçük
 * harf de karışık olmalı. UUID, kısıt adı (crew_movement_events_key) ve uzun işlev adı kalır.
 */
function looksRandom(run: string): boolean {
  const core = run.replace(/=+$/, '');
  if (UUID_ONLY.test(core)) return false;
  if (/^[0-9a-fA-F]+$/.test(core)) return true;
  if (!/\d/.test(core) || !/[A-Za-z]/.test(core)) return false;
  if (!/[-_]/.test(core)) return true;
  return /[a-z]/.test(core) && /[A-Z]/.test(core);
}

/** Harf sınıfı (küçük, büyük, rakam) değişimlerinin oranı; rastgele dizide ~0,6, camelCase'te ~0,3. */
function classChangeRatio(piece: string): number {
  const kind = (char: string) => (/[a-z]/.test(char) ? 0 : /[A-Z]/.test(char) ? 1 : 2);
  let changes = 0;
  for (let i = 1; i < piece.length; i += 1) {
    if (kind(piece[i] ?? '') !== kind(piece[i - 1] ?? '')) changes += 1;
  }
  return piece.length > 1 ? changes / (piece.length - 1) : 0;
}

/**
 * Uzun dizi rastgele mi: `+` ve `/` yoksa `looksRandom`. Varsa standart base64 ya da dosya yolu
 * olabilir: `-`/`_` içeren yol yalnız 32+ karakterlik rastgele bölümüyle, saf base64 alfabesi ise
 * küçük harf, büyük harf ve rakamı sık sık değişen 8+ karakterlik bir bölümüyle maskelenir. Böylece
 * `/dist/office/VesselSetupChanges` kalır.
 */
function looksRandomRun(run: string): boolean {
  if (!/[+/]/.test(run)) return looksRandom(run);
  const pieces = run.replace(/=+$/, '').split(/[+/]/);
  if (/[-_]/.test(run)) return pieces.some((piece) => piece.length >= 32 && looksRandom(piece));
  return pieces.some(
    (piece) =>
      piece.length >= 8 &&
      /[a-z]/.test(piece) &&
      /[A-Z]/.test(piece) &&
      /\d/.test(piece) &&
      classChangeRatio(piece) >= 0.4,
  );
}

/**
 * Metindeki sırları `<redacted>` ile değiştirir: Bearer/Basic belirteci; sır anahtarlarının
 * (password, passwd, pwd, pass, secret, token, api key, authorization, cookie, set-cookie, session,
 * totp, otp, code ve accessToken, db_password, PGPASSWORD, x-api-key gibi bileşikleri) boşluklu
 * olabilen değeri; URL'deki parola; JWT benzeri dizi; Buffer baytları; tireli 4'erli parola;
 * eşleştirme ve kurtarma kodu; 32+ karakterlik rastgele base64/base64url/hex dizisi.
 * UUID'ler, hata kodları ve doğal metin korunur; iki kez uygulamak sonucu değiştirmez.
 */
export function redactSecrets(text: string): string {
  if (text.length === 0) return text;
  let out = text.replace(
    URL_CREDENTIALS,
    (_whole, scheme: string, user: string) => `${scheme}${user}:${REDACTED_TEXT}@`,
  );
  // Doğal metindeki küçük harfli sözcük ("basic validation", "bearer token missing") kalır
  out = out.replace(AUTH_SCHEME, (whole, scheme: string, space: string, token: string) =>
    /^[a-z]+$/.test(token) ? whole : `${scheme}${space}${REDACTED_TEXT}`,
  );
  out = out.replace(JWT_LIKE, REDACTED_TEXT);
  out = out.replace(BUFFER_VIEW, `<Buffer ${REDACTED_TEXT}>`);
  out = redactKeyValues(out);
  out = out.replace(GROUPED_PASSWORD, REDACTED_TEXT);
  out = out.replace(PAIRING_CODE, REDACTED_TEXT).replace(RECOVERY_CODE, REDACTED_TEXT);
  return out.replace(LONG_RUN, (run) => (looksRandomRun(run) ? REDACTED_TEXT : run));
}

/** En çok `max` karakter; kırpılan metin `…` ile biter, vekil çifti bölünmez. */
export function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  if (max <= 0) return '';
  let end = max - 1;
  const code = text.charCodeAt(end - 1);
  if (end > 0 && code >= 0xd800 && code <= 0xdbff) end -= 1;
  return `${text.slice(0, end)}…`;
}

/**
 * Kayda girecek metin: önce sırlar temizlenir (`redactSecrets`), sonra en çok `max` karaktere
 * kırpılır. Çok uzun metin önce kabaca kesilir; düzenli ifadeler sınırlı metinde çalışır.
 */
export function cleanProblemText(value: string, max: number): string {
  const bound = max * 4 + 1000;
  return truncateText(redactSecrets(value.length > bound ? value.slice(0, bound) : value), max);
}

/** İlk anlamlı satır; ilk satır yalnız noktalamaysa (ör. `[`) bütün metin tek satıra iner. */
export function firstProblemLine(text: string): string {
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    return /[\p{L}\p{N}]/u.test(trimmed) ? trimmed : text.replace(/\s+/g, ' ').trim();
  }
  return '';
}

const ROUTE_ORIGIN = /^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/?#]*/;

function normalizeRouteSegment(segment: string): string {
  if (segment.length === 0 || segment.startsWith(':')) return segment;
  if (UUID_ONLY.test(segment)) return ':id';
  if (/^\d+(?:\.\d+)*$/.test(segment)) return ':n';
  if (/^[0-9a-fA-F]{16,}$/.test(segment)) return ':hex';
  if (segment.includes('@') || /%40/i.test(segment)) return ':email';
  if (segment.length >= 20 && /^[A-Za-z0-9_-]+$/.test(segment) && looksRandom(segment)) {
    return ':token';
  }
  return segment.replace(UUID_ANYWHERE, ':id');
}

/**
 * Gruplanabilir rota: "METHOD /yol". Sorgu dizesi ve # atılır, mutlak adresin kökeni kalkar;
 * UUID → :id, sayı ya da sürüm (1.0.3388) → :n, 16+ hex → :hex, e-posta → :email, rastgele
 * belirteç → :token. Parametreli rota (/vessels/:vesselId) olduğu gibi kalır. En çok 200 karakter.
 */
export function normalizeRoute(
  method: string | null | undefined,
  url: string | null | undefined,
): string | null {
  if (typeof url !== 'string' || url.trim().length === 0) return null;
  let path = url.trim().replace(ROUTE_ORIGIN, '');
  const cut = path.search(/[?#]/);
  if (cut !== -1) path = path.slice(0, cut);
  if (path.length === 0) path = '/';
  const normalized = path.split('/').map(normalizeRouteSegment).join('/');
  const verb = typeof method === 'string' ? method.trim().toUpperCase() : '';
  return truncateText(verb.length > 0 ? `${verb} ${normalized}` : normalized, PROBLEM_LIMITS.route);
}

/** Rota metni ("POST /x/1?y=2" ya da yalnız yol) → `normalizeRoute`; metin değilse null. */
export function normalizeRouteText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const parts = /^([A-Za-z]+)\s+(\S+)$/.exec(value.trim());
  return parts === null ? normalizeRoute(null, value) : normalizeRoute(parts[1], parts[2]);
}

const ISO_TIME = /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?/g;
const EMAIL_ANYWHERE = /[^\s@"'`<>()[\]]+@[^\s@"'`<>()[\]]+\.[^\s@"'`<>()[\]]+/g;
const QUOTED_TEXT = /"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|`(?:[^`\\\n]|\\.)*`/g;
const HEX_TEXT = /\b0x[0-9a-fA-F]+\b|\b[0-9a-fA-F]{8,}\b/g;
const STACK_FRAME = /^\s*at\s/;

/**
 * İletinin değişken kısımları yer tutucuya iner: aynı hata farklı kimlik ve sayıyla aynı izi verir.
 * Kayıttaki ileti zaten en çok 500 karakter; sınır düzenli ifadeleri kısa metinde tutar.
 */
function fingerprintMessage(message: string): string {
  return firstProblemLine(message)
    .slice(0, PROBLEM_LIMITS.message * 2)
    .replace(ISO_TIME, '<time>')
    .replace(UUID_ANYWHERE, '<uuid>')
    .replace(EMAIL_ANYWHERE, '<email>')
    .replace(QUOTED_TEXT, '<str>')
    .replace(HEX_TEXT, '<hex>')
    .replace(/\d+/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Çerçevenin yeri: satır:sütun, file://, sürücü harfi ve kurulum yolu atılır. Çalışma alanı
 * dosyası `apps/…`/`packages/…`, paket dosyası son `node_modules/…` ile başlar; diğer mutlak yollarda
 * yalnız dosya adı kalır (/srv/felix/builds/<sürüm>/apps/server/dist/x.js → apps/server/dist/x.js).
 */
function normalizeLocation(location: string): string {
  let path = location
    .replace(/^file:\/\//, '')
    .replace(/\\/g, '/')
    .replace(/(?::\d+){1,2}$/, '');
  if (!/^\/|^[A-Za-z]:\//.test(path)) return path;
  path = path.replace(/^\/?[A-Za-z]:\//, '/');
  const modules = path.lastIndexOf('/node_modules/');
  if (modules !== -1) return path.slice(modules + 1);
  const workspace = path.search(/\/(?:apps|packages)\//);
  if (workspace !== -1) return path.slice(workspace + 1);
  return path.slice(path.lastIndexOf('/') + 1);
}

function normalizeFrame(line: string): string {
  const frame = line.trim().replace(/^at\s+/, '');
  const located = /^(.*?)\s+\((.*)\)$/.exec(frame);
  if (located === null) return normalizeLocation(frame);
  return `${located[1]} (${normalizeLocation(located[2] ?? '')})`;
}

/** Parmak izine giren çerçeve sayısı. */
const FINGERPRINT_FRAMES = 3;

function fingerprintFrames(stack: string | null): string[] {
  if (stack === null) return [];
  const frames: string[] = [];
  for (const line of stack.split(/\r?\n/)) {
    if (!STACK_FRAME.test(line)) continue;
    frames.push(normalizeFrame(line));
    if (frames.length === FINGERPRINT_FRAMES) break;
  }
  return frames;
}

/**
 * Yığının parmak izine giren kısmı: ilk üç çerçeveye kadarki satırlar (başlık dahil). Gemi aynı
 * hatanın saat içindeki tekrarında yığının geri kalanını göndermez (uydu hattı, ADR 0149 §9); bulut
 * kısaltılmış yığından aynı izi hesaplar. Çerçevesiz yığın olduğu gibi kalır.
 */
export function fingerprintStack(stack: string | null): string | null {
  if (stack === null) return null;
  const lines = stack.split(/\r?\n/);
  let frames = 0;
  for (const [index, line] of lines.entries()) {
    if (STACK_FRAME.test(line)) frames += 1;
    if (frames === FINGERPRINT_FRAMES) return lines.slice(0, index + 1).join('\n');
  }
  return stack;
}

const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const UINT64_MASK = 0xffffffffffffffffn;

/** FNV-1a 64 bit, UTF-8 baytları üzerinde; 16 haneli onaltılık. */
function fnv1a64(input: string): string {
  let hash = FNV_OFFSET;
  const add = (byte: number) => {
    hash = ((hash ^ BigInt(byte)) * FNV_PRIME) & UINT64_MASK;
  };
  for (const char of input) {
    const point = char.codePointAt(0) ?? 0;
    if (point < 0x80) {
      add(point);
    } else if (point < 0x800) {
      add(0xc0 | (point >> 6));
      add(0x80 | (point & 0x3f));
    } else if (point < 0x10000) {
      add(0xe0 | (point >> 12));
      add(0x80 | ((point >> 6) & 0x3f));
      add(0x80 | (point & 0x3f));
    } else {
      add(0xf0 | (point >> 18));
      add(0x80 | ((point >> 12) & 0x3f));
      add(0x80 | ((point >> 6) & 0x3f));
      add(0x80 | (point & 0x3f));
    }
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * Gruplama anahtarı "<app>:<16 hex>": uygulama, kaynak, tür, rota, normalize ileti ve yığının ilk
 * üç çerçevesi (yer bilgisi sürümden ve kurulum yolundan arındırılmış) üzerinde FNV-1a 64. Aynı
 * hatanın farklı UUID/sayı/sürümle tekrarı aynı izi, farklı yerden atılan aynı ileti farklı izi verir.
 */
export function errorFingerprint(
  e: Pick<CapturedError, 'app' | 'source' | 'kind' | 'message' | 'stack' | 'route'>,
): string {
  const basis = [
    e.app,
    e.source,
    e.kind ?? '',
    e.route ?? '',
    fingerprintMessage(e.message),
    fingerprintFrames(e.stack).join('\n'),
  ].join('|');
  return `${e.app}:${fnv1a64(basis)}`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyText(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function uuidOf(value: unknown): string | null {
  return typeof value === 'string' && UUID_ONLY.test(value) ? value.toLowerCase() : null;
}

function statusOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 100 && value <= 599
    ? value
    : null;
}

function requestIdOf(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' && value.length > 0
    ? truncateText(value, PROBLEM_LIMITS.reqId)
    : null;
}

function logTime(value: unknown): Date | null {
  const ms =
    typeof value === 'number' ? value : typeof value === 'string' ? Date.parse(value) : Number.NaN;
  return Number.isFinite(ms) ? new Date(ms) : null;
}

/** pino `err` nesnesinin türü: type (ya da name) ve varsa ':' + code. */
function errorKind(err: Record<string, unknown> | null): string | null {
  if (err === null) return null;
  const name = nonEmptyText(err.type) ?? nonEmptyText(err.name);
  const code = typeof err.code === 'string' || typeof err.code === 'number' ? String(err.code) : '';
  const kind = name === null ? code : code.length > 0 ? `${name}:${code}` : name;
  return kind.length > 0 ? truncateText(kind, PROBLEM_LIMITS.kind) : null;
}

/**
 * Süreç içi günlüğün (pino/Fastify) tek satırı → hata kaydı. Yalnız level >= 50 (50 error,
 * 60 fatal); "[problems]" ile başlayan ve bozuk satır null. İleti err.message (yoksa msg), ilk
 * anlamlı satır; tür err.type/err.name + ':' + err.code; rota req.method + req.url ya da satırdaki
 * `route`; durum kodu res.statusCode ?? err.statusCode ?? statusCode. Şirket ve gemi kimliği
 * (tenantId|tenant_id|tenant, vesselId|vessel_id) yalnız UUID ise alınır. Metinler temizlenir ve
 * kırpılır; zaman satırdaki `time`, yoksa `ctx.now`.
 */
export function parsePinoLine(
  line: string,
  ctx: { app: ProblemApp; version: string | null; host: string | null; now?: Date },
): CapturedError | null {
  // Hızlı ön eleme: info/debug satırı ayrıştırılmaz
  if (!line.includes('"level":5') && !line.includes('"level":6')) return null;
  let entry: Record<string, unknown> | null;
  try {
    entry = asRecord(JSON.parse(line));
  } catch {
    return null;
  }
  if (entry === null || typeof entry.level !== 'number' || entry.level < 50) return null;
  const msg = typeof entry.msg === 'string' ? entry.msg : '';
  const err = asRecord(entry.err) ?? asRecord(entry.error);
  const errMessage = typeof err?.message === 'string' ? err.message : '';
  if (msg.startsWith(PROBLEMS_LOG_PREFIX) || errMessage.startsWith(PROBLEMS_LOG_PREFIX)) {
    return null;
  }
  const req = asRecord(entry.req);
  const res = asRecord(entry.res);
  const detail: Record<string, unknown> = {};
  if (errMessage.length > 0 && msg.length > 0 && msg !== errMessage) {
    detail.msg = cleanProblemText(msg, PROBLEM_LIMITS.message);
  }
  if (typeof entry.pid === 'number') detail.pid = entry.pid;
  const requestRoute =
    req === null ? null : normalizeRoute(nonEmptyText(req.method), nonEmptyText(req.url));
  return {
    at: logTime(entry.time) ?? ctx.now ?? new Date(),
    app: ctx.app,
    source: 'log',
    level: entry.level >= 60 ? 'fatal' : 'error',
    kind: errorKind(err),
    message: cleanProblemText(
      firstProblemLine(errMessage) || firstProblemLine(msg),
      PROBLEM_LIMITS.message,
    ),
    route: requestRoute ?? normalizeRouteText(entry.route),
    statusCode:
      statusOf(res?.statusCode) ?? statusOf(err?.statusCode) ?? statusOf(entry.statusCode),
    stack:
      typeof err?.stack === 'string' ? cleanProblemText(err.stack, PROBLEM_LIMITS.stack) : null,
    reqId: requestIdOf(entry.reqId),
    host: ctx.host,
    version: ctx.version,
    tenantId: uuidOf(entry.tenantId ?? entry.tenant_id ?? entry.tenant),
    vesselId: uuidOf(entry.vesselId ?? entry.vessel_id),
    detail: Object.keys(detail).length > 0 ? detail : null,
  };
}

/** Node'un yakalanmamış hata başlığı: "dosya:satır", ardından kaynak satırı ve `^` satırı. */
const NODE_SOURCE_HEADER = /^(?:file:\/\/|node:|[A-Za-z]:[\\/]|[\\/])\S.*:\d+$/;
const CARET_LINE = /^\s*\^+\s*$/;
const WARNING_LINE = /^\(node:\d+\)\s+(?:\[(\w+)\]\s+)?(\w*Warning):/;
const ERROR_HEADLINE = /^\[?(?:Uncaught\s+)?(\w*(?:Error|Exception|Rejection))(?:\s?\[(\w+)\])?:/;

function continuesEntry(line: string): boolean {
  return (
    /^\s/.test(line) ||
    /^at\s/.test(line) ||
    /^[}\]]/.test(line) ||
    /^caused by/i.test(line) ||
    line.startsWith('Node.js v') ||
    line.startsWith('(Use `node --trace-')
  );
}

/**
 * stderr metnini kayıtlara böler. Boşlukla ya da "at " ile başlayan, "}"/"]" ile başlayan,
 * "Caused by", "Node.js v…" ve "(Use `node --trace-…" satırları öncekine eklenir; boş ve
 * "[problems]" satırları atılır. Node'un çökme başlığı (dosya:satır, kaynak satırı, `^`) izleyen
 * hatanın başına eklenir.
 */
export function splitStderrEntries(text: string): string[] {
  const lines = text.split(/\r?\n/);
  const entries: string[] = [];
  let current: string[] | null = null;
  let context: string[] | null = null;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';
    if (line.trim().length === 0 || line.trimStart().startsWith(PROBLEMS_LOG_PREFIX)) continue;
    const sourceLine = lines[i + 1];
    const caret = lines[i + 2];
    if (
      NODE_SOURCE_HEADER.test(line) &&
      sourceLine !== undefined &&
      caret !== undefined &&
      CARET_LINE.test(caret)
    ) {
      if (current !== null) entries.push(current.join('\n'));
      if (context !== null) entries.push(context.join('\n'));
      current = null;
      context = [line, sourceLine, caret];
      i += 2;
      continue;
    }
    if (current !== null && continuesEntry(line)) {
      current.push(line);
      continue;
    }
    if (current !== null) entries.push(current.join('\n'));
    current = context === null ? [line] : [...context, line];
    context = null;
  }
  if (current !== null) entries.push(current.join('\n'));
  if (context !== null) entries.push(context.join('\n'));
  return entries;
}

/**
 * Tek stderr kaydı → hata kaydı. "{" ile başlayan pino satırı `parsePinoLine` ile okunur (info
 * satırı null). ExperimentalWarning null; "(node:123) [DEP0005] DeprecationWarning:" warn
 * (tür DeprecationWarning:DEP0005); diğerleri error. Tür ilk satırdaki "TypeError [ERR_X]:" →
 * TypeError:ERR_X ya da "Uncaught"; ileti ilk satır, yığın bütün kayıt (temizlenmiş, kırpılmış).
 */
export function stderrEntryToError(
  entry: string,
  ctx: { app: ProblemApp; version: string | null; host: string | null; at: Date },
): CapturedError | null {
  const lines = entry.split(/\r?\n/);
  const header =
    lines.length > 3 && NODE_SOURCE_HEADER.test(lines[0] ?? '') && CARET_LINE.test(lines[2] ?? '');
  const first = firstProblemLine((header ? lines.slice(3) : lines).join('\n'));
  if (first.length === 0) return null;
  if (first.startsWith('{') && first.includes('"level":')) {
    const parsed = parsePinoLine(first, {
      app: ctx.app,
      version: ctx.version,
      host: ctx.host,
      now: ctx.at,
    });
    return parsed === null ? null : { ...parsed, source: 'stderr' };
  }
  if (first.includes('ExperimentalWarning')) return null;
  const warning = WARNING_LINE.exec(first);
  const error = warning === null ? ERROR_HEADLINE.exec(first) : null;
  let kind: string | null = null;
  if (warning !== null) kind = warning[1] ? `${warning[2]}:${warning[1]}` : (warning[2] ?? null);
  else if (error !== null) kind = error[2] ? `${error[1]}:${error[2]}` : (error[1] ?? null);
  else if (/^\[?Uncaught\b/.test(first)) kind = 'Uncaught';
  return {
    at: ctx.at,
    app: ctx.app,
    source: 'stderr',
    level: warning === null ? 'error' : 'warn',
    kind: kind === null ? null : truncateText(kind, PROBLEM_LIMITS.kind),
    message: cleanProblemText(first, PROBLEM_LIMITS.message),
    route: null,
    statusCode: null,
    stack: cleanProblemText(entry.trim(), PROBLEM_LIMITS.stack),
    reqId: null,
    host: ctx.host,
    version: ctx.version,
    tenantId: null,
    vesselId: null,
    detail: null,
  };
}

const DETAIL_MAX_DEPTH = 3;
const DETAIL_MAX_KEYS = 30;
const DETAIL_MAX_ITEMS = 20;
const DETAIL_TEXT_MAX = 500;
/**
 * ADR 0149 §2 (ADR 0039 §9 kalıbı): anahtarda bunlardan biri geçerse (harf duyarsız, alt dize)
 * anahtar ayrıntıdan çıkarılır. Veritabanı katmanı aynı kuralı yeniden uygular.
 */
const DETAIL_SECRET_PART = /password|passwd|secret|token|cookie|code|otp|digest|authorization/i;
/** Sır sayılmayan anahtarlar: `passwordHints` işaretleri, yanıt ve durum kodu (ADR 0149 §2). */
const DETAIL_SAFE_KEYS: ReadonlySet<string> = new Set([
  'passwordWhitespace',
  'temporaryShape',
  'responseCode',
  'statusCode',
]);

/** ADR kuralına ek olarak metin temizliğinin sözcük kuralı da uygulanır (pwd, apiKey, sessionId). */
function isSecretDetailKey(key: string): boolean {
  if (DETAIL_SAFE_KEYS.has(key)) return false;
  return DETAIL_SECRET_PART.test(key) || isSecretKey(keyWords(key));
}

/** JSON metninin UTF-8 bayt uzunluğu (Buffer ve TextEncoder olmadan; domain I/O'suzdur). */
function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && index + 1 < text.length) {
      const next = text.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        index += 1;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

function sanitizeDetailValue(value: unknown, depth: number): unknown {
  if (value === null) return null;
  switch (typeof value) {
    case 'string':
      return cleanProblemText(value, DETAIL_TEXT_MAX);
    case 'number':
      return Number.isFinite(value) ? value : null;
    case 'boolean':
      return value;
    case 'bigint':
      return value.toString();
    case 'object':
      break;
    default:
      return undefined;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (value instanceof Error) {
    return cleanProblemText(`${value.name}: ${value.message}`, DETAIL_TEXT_MAX);
  }
  if (depth >= DETAIL_MAX_DEPTH) return '[…]';
  if (Array.isArray(value)) {
    return value
      .slice(0, DETAIL_MAX_ITEMS)
      .map((item) => sanitizeDetailValue(item, depth + 1) ?? null);
  }
  return sanitizeDetailObject(value as Record<string, unknown>, depth + 1);
}

function sanitizeDetailObject(
  record: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  let count = 0;
  for (const [key, value] of Object.entries(record)) {
    if (count >= DETAIL_MAX_KEYS) break;
    if (key === '__proto__' || isSecretDetailKey(key)) continue;
    const clean = sanitizeDetailValue(value, depth);
    if (clean === undefined) continue;
    out[truncateText(key, 64)] = clean;
    count += 1;
  }
  return out;
}

/**
 * Kayda girecek ayrıntı nesnesi; düz nesne değilse ya da boşsa null. Sır anahtarları iç içe
 * düzeylerde de çıkarılır (ADR 0149 §2: password, passwd, secret, token, cookie, code, otp, digest,
 * authorization alt dizesi; ayrıca pwd, apiKey, sessionId gibi sözcükler). `passwordWhitespace`,
 * `temporaryShape`, `responseCode` ve `statusCode` kalır. Metinler temizlenmiş ve en çok 500
 * karakter; en çok 3 düzey, düzey başına 30 anahtar, dizi başına 20 öğe. JSON'u UTF-8 olarak
 * `maxJsonLength` baytı (2 KB) aşarsa yalnız `{ truncated: true }`. İki kez uygulamak sonucu
 * değiştirmez.
 */
export function sanitizeProblemDetail(
  detail: unknown,
  maxJsonLength: number = PROBLEM_LIMITS.detailJson,
): Record<string, unknown> | null {
  const record = asRecord(detail);
  if (record === null || record instanceof Date || record instanceof Error) return null;
  const copy = sanitizeDetailObject(record, 1);
  if (Object.keys(copy).length === 0) return null;
  return utf8ByteLength(JSON.stringify(copy)) <= maxJsonLength ? copy : { truncated: true };
}
